import { createConfigParametersRepository } from '../../configParametersRepository';
import { createFirebaseAdapter } from '../firebase/firebaseAdapter';
import { createFirebaseClient } from '../firebase/firebaseClient';
import { getUserPropertiesFromDimensions } from '../google-analytics/ga4';
import type { CustomParams } from '../google-analytics/ga4Types';
import { trackUserProperties } from '../google-analytics/userProperties';
import { getPageLocation, gtagWrapper } from '../google-analytics/utils';
import { checkGaInit } from '../init/ga';
import { createRuntimeGoogleState } from '../init/ga/state';
import { CUSTOM_PARAMS_STORAGE_KEY } from '../resources/constants';
import { handleEventError } from './error';
import type { TrackEventArguments } from './types';

const getStoredScreenName = (): string | undefined => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const trackingStorageClient = getTrackingStorageClient();
  const storedCustomParams = trackingStorageClient?.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  );
  return storedCustomParams?.screen_name;
};

export const trackEvent = async ({
  action,
  label,
  page,
  category = 'NOT_DEFINED',
  dimensionsAndParams = {},
}: TrackEventArguments) => {
  if (typeof window === 'undefined') return;
  await checkGaInit();
  const { getGoogleState, queueEvent } = createRuntimeGoogleState();
  const { hasThePageBeenTracked } = getGoogleState();

  if (!hasThePageBeenTracked) {
    queueEvent({
      action: action || '',
      label: label || '',
      page,
      category,
    });
    return;
  }

  const options: TrackEventArguments = {
    action: action || '',
    label: label || '',
    category,
    page: page || getStoredScreenName(),
    dimensionsAndParams,
  };

  trackEventInGa4(options);
};

/**
 * Tracks a Google Analytics 4 event.
 * @param data
 */
export const trackEventInGa4 = (data: TrackEventArguments) => {
  const {
    page: screen_name,
    category: event_category,
    action: event_action,
    label: event_label,
    dimensionsAndParams,
  } = data;
  handleEventError({
    action: event_action,
    label: event_label,
    page: screen_name,
    category: event_category,
  });
  if (!screen_name) return;
  const firebaseAdapter = createFirebaseAdapter();
  const firebaseClient = createFirebaseClient(firebaseAdapter);
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const { getTrackingStoredValue } = getTrackingStorageClient() || {};
  const storedCustomParams = getTrackingStoredValue?.(
    CUSTOM_PARAMS_STORAGE_KEY
  );

  const customParams: CustomParams = {
    ...storedCustomParams,
    screen_name,
    event_category,
    event_action,
    event_label,
  };
  trackUserProperties(getUserPropertiesFromDimensions(dimensionsAndParams));
  firebaseClient.trackEvent(customParams);
  gtagWrapper('event', 'edo_event', {
    page_location: getPageLocation(screen_name),
    ...customParams,
  });
};
